import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, MessageSquare } from 'lucide-react';
import contactBg from '../assets/images/contact-bg.png';

const ContactSection = styled.section`
  padding-top: 12rem;
  background-color: var(--bg-primary);
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${contactBg});
    background-size: cover;
    background-position: center;
    opacity: 0.1;
    z-index: 0;
    mix-blend-mode: screen;
  }
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: 10%;
  right: -10%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%);
  z-index: 0;
  pointer-events: none;
`;

const ContactContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 4rem;
  align-items: start;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactInfo = styled(motion.div)`
  .contact-card {
    padding: 3rem 2.5rem;
    margin-bottom: 2rem;

    @media (max-width: 480px) {
      padding: 2rem 1.5rem;
    }
  }

  h3 {
    color: var(--text-primary);
    font-size: 1.5rem;
    margin-bottom: 2rem;
    font-weight: 600;
  }

  .contact-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 2rem;
    
    .icon-wrapper {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      background: rgba(59, 130, 246, 0.1);
      color: var(--accent-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 1.5rem;
      flex-shrink: 0;
    }
    
    .details {
      h4 {
        color: var(--text-primary);
        margin-bottom: 0.5rem;
        font-weight: 600;
        font-size: 1.1rem;
      }
      
      p {
        color: var(--text-secondary);
        margin: 0;
        line-height: 1.5;
      }
    }
  }
`;

const ContactForm = styled(motion.form)`
  padding: 3.5rem;

  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
  }

  h3 {
    color: var(--text-primary);
    font-size: 2rem;
    margin-bottom: 2.5rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .form-group {
    margin-bottom: 1.8rem;
    position: relative;

    label {
      display: block;
      color: var(--text-secondary);
      margin-bottom: 0.8rem;
      font-weight: 500;
      font-size: 0.95rem;
    }

    input,
    select,
    textarea {
      width: 100%;
      padding: 1rem 1.2rem;
      border: 1px solid var(--border-subtle);
      border-radius: 12px;
      background-color: rgba(255, 255, 255, 0.02);
      color: var(--text-primary);
      font-family: inherit;
      font-size: 1rem;
      transition: all 0.3s ease;

      &:focus {
        outline: none;
        border-color: var(--accent-primary);
        background-color: rgba(59, 130, 246, 0.05);
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
      }

      &::placeholder {
        color: var(--text-tertiary);
      }
    }

    textarea {
      min-height: 150px;
      resize: vertical;
    }

    select {
      cursor: pointer;
      appearance: none;
    }
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .submit-btn {
    width: 100%;
    margin-top: 1rem;
    padding: 1.2rem;
    font-size: 1.1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.8rem;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  color: #34d399;
  padding: 1.2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 500;
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        subject: '',
        message: ''
      });

      // Hide success message after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000);
    }, 1500);
  };

  const contactInfo = [
    {
      icon: <Mail size={24} />,
      title: 'Email Direct',
      detail: 'takeoff@contextjetai.services',
      subtitle: 'Our primary channel. Expect a response within 4 hours.'
    },
    {
      icon: <MapPin size={24} />,
      title: 'Location',
      detail: 'Registered in India',
      subtitle: 'Serving B2B clients across India, the United States, UAE, and global markets.'
    }
  ];

  return (
    <ContactSection>
      <BackgroundGlow />
      <div className="container" style={{ maxWidth: '1200px' }}>
        <motion.div
          style={{ textAlign: 'center', marginBottom: '5rem', position: 'relative', zIndex: 1 }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-subtitle">Initiate Contact</h2>
          <h1 className="section-title">Engage with <span className="gradient-text-accent">ContextJet AI</span></h1>
          <p className="section-description" style={{ margin: '0 auto', fontSize: '1.2rem' }}>
            Ready to deploy enterprise-grade intelligence? Our engineers are standing by
            to evaluate your infrastructure and architect a custom solution.
          </p>
        </motion.div>

        <ContactContainer>
          <ContactInfo
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="card glass contact-card">
              <h3>Direct Channels</h3>
              {contactInfo.map((info, index) => (
                <div key={index} className="contact-item">
                  <div className="icon-wrapper">{info.icon}</div>
                  <div className="details">
                    <h4>{info.title}</h4>
                    <p style={{ color: 'var(--text-primary)', fontWeight: '500', marginBottom: '4px' }}>{info.detail}</p>
                    <p style={{ fontSize: '0.9rem' }}>{info.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="card glass" style={{ padding: '2rem 2.5rem' }}>
              <h4 style={{ color: 'var(--text-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <MessageSquare size={20} color="var(--accent-primary)" />
                Technical Support
              </h4>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.6' }}>
                Existing clients requiring urgent technical assistance should use their dedicated secure portal or contact their assigned engineering lead directly.
              </p>
            </div>
          </ContactInfo>

          <ContactForm
            className="card glass"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3>Deployment Inquiry</h3>

            {isSubmitted && (
              <SuccessMessage
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                Transmission successful. An engineering lead will review your request and contact you shortly.
              </SuccessMessage>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="e.g. Sarah"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="e.g. Connor"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Work Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="sarah@enterprise.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="company">Organization</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Cyberdyne Systems"
              />
            </div>

            <div className="form-group">
              <label htmlFor="subject">Inquiry Type</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              >
                <option value="" disabled>Select inquiry scope</option>
                <option value="demo">Architecture Demo Request</option>
                <option value="pricing">Enterprise Pricing Evaluation</option>
                <option value="technical">Technical Capability Query</option>
                <option value="partnership">Strategic Partnership</option>
                <option value="general">General Inquiry</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message">Project Specifications</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Detail your use case, current tech stack, and the scale of data involved..."
                required
              />
            </div>

            <button
              type="submit"
              className="glow-btn submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Transmitting Data...' : 'Initialize Contact'}
            </button>
          </ContactForm>
        </ContactContainer>
      </div>
    </ContactSection>
  );
};

export default Contact;